import { CustomerTier, PaginatedResponse } from "@/api.types";
import { useSession } from "@/services/auth0/session";
import { useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import { z } from "zod";

export type CustomerTierCreateFormSchema = z.infer<typeof customerTierSchema>;
export const customerTierSchema = z.object({
  name: z.string().min(1, "Name is required"),
  type: z.enum(["CUSTOM", "DEFAULT"]).default("CUSTOM"),
  description: z.string().optional(),
  discountPercentage: z.number().min(0).max(1).default(0),
  freePickups: z.number().min(0).default(0),
  prioritySupport: z.boolean().default(false),
  minimumSpend: z.number().min(0).optional(),
  minimumShipments: z.number().min(0).optional(),
  isActive: z.boolean().default(true),
});

export const useCreateCustomerTier = ({
  onSuccess,
  onError,
}: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
} = {}) => {
  const { token } = useSession();

  return useMutation({
    mutationFn: async (data: CustomerTierCreateFormSchema) => {
      const response = await axios.post<CustomerTier>(
        `${import.meta.env.VITE_API_URL}/api/v1/customer-tiers`,
        data,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      return response.data;
    },
    onSuccess: () => {
      onSuccess?.();
    },
    onError: (error) => {
      onError?.(error);
    },
  });
};

export function useCustomerTiers() {
  const { token } = useSession();
  return useQuery({
    queryKey: ["customerTiers"],
    queryFn: async () => {
      const response = await axios.get<PaginatedResponse<CustomerTier>>(
        `${import.meta.env.VITE_API_URL}/api/v1/customer-tiers`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    },
    enabled: !!token,
  });
}
